/**
 * Phase 4.4.1: Clinician Invite Code Management UI
 *
 * Allows clinicians to generate, view, copy, and revoke invite codes
 * for private clinician access.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  ClipboardDocumentIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import { inviteCodeAPI, InviteCode, InviteCodeCreate, InviteCodeListResponse } from '../../services/api';
import { formatClinicDateTime } from '../../utils/timezone';
import Card from '../ui/Card';
import Button from '../ui/Button';
import StatusBanner from '../ui/StatusBanner';
import Modal from '../ui/Modal';
import FormField, { Input, Select } from '../ui/FormField';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../../utils/apiError';

interface GenerateCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (data: InviteCodeCreate) => Promise<void>;
  isGenerating: boolean;
}

const GenerateCodeModal: React.FC<GenerateCodeModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  isGenerating,
}) => {
  const [intendedEmail, setIntendedEmail] = useState('');
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(7);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onGenerate({
      intended_email: intendedEmail || undefined,
      label: label || undefined,
      expires_in_days: expiresInDays,
    });
    // Reset form
    setIntendedEmail('');
    setLabel('');
    setExpiresInDays(7);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Invite Code"
    >
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <FormField label="Client Email (Optional)" htmlFor="intendedEmail" helper="For your records only. Not enforced.">
            <Input
              id="intendedEmail"
              type="email"
              value={intendedEmail}
              onChange={(e) => setIntendedEmail(e.target.value)}
              placeholder="client@example.com"
            />
          </FormField>

          <FormField label="Label (Optional)" htmlFor="label" helper="Internal note to help you remember who this code is for.">
            <Input
              id="label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., John Doe - Initial Visit"
              maxLength={100}
            />
          </FormField>

          <FormField label="Expires In" htmlFor="expiresIn">
            <Select
              id="expiresIn"
              value={expiresInDays.toString()}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
              options={[
                { value: '1', label: '1 day' },
                { value: '3', label: '3 days' },
                { value: '7', label: '7 days' },
                { value: '14', label: '14 days' },
                { value: '30', label: '30 days' },
              ]}
            />
          </FormField>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isGenerating}>
            {isGenerating ? 'Generating...' : 'Generate Code'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface CodeCardProps {
  code: InviteCode;
  onRevoke: (id: number) => void;
  isRevoking: boolean;
}

const CodeCard: React.FC<CodeCardProps> = ({ code, onRevoke, isRevoking }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code.formatted_code);
      setCopied(true);
      toast.success('Code copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy code');
    }
  };

  const isExpired = new Date(code.expires_at) < new Date();
  const isUsed = code.times_used >= code.max_uses;
  const isRevoked = !!code.revoked_at;
  const canRevoke = code.is_active && !isUsed && !isRevoked;

  const getStatusBadge = () => {
    if (isRevoked) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-muted/20 text-clinic-muted">
          Revoked
        </span>
      );
    }
    if (isUsed) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-primary/20 text-clinic-primary">
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          Used
        </span>
      );
    }
    if (isExpired) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-accent/20 text-clinic-accent">
          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
          Expired
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-gold/30 text-clinic-dark">
        Active
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    // Use clinic timezone for consistent display
    return formatClinicDateTime(dateStr, 'MMM d, yyyy h:mm a');
  };

  return (
    <Card className="p-4 hover:shadow-clinic transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {/* Code display */}
          <div className="flex items-center space-x-2 mb-2">
            <code className="text-lg font-mono font-semibold px-3 py-1 rounded bg-clinic-cream text-clinic-dark">
              {code.formatted_code}
            </code>
            <button
              onClick={handleCopy}
              className="p-1.5 rounded-md hover:bg-clinic-cream transition-colors"
              title="Copy code"
            >
              {copied ? (
                <CheckCircleIcon className="h-5 w-5 text-clinic-primary" />
              ) : (
                <ClipboardDocumentIcon className="h-5 w-5 text-clinic-muted" />
              )}
            </button>
          </div>

          {/* Label */}
          {code.label && (
            <p className="text-sm text-clinic-dark mb-1 font-medium">{code.label}</p>
          )}

          {/* Intended email */}
          {code.intended_email && (
            <p className="text-sm text-clinic-muted mb-1">
              For: {code.intended_email}
            </p>
          )}

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-clinic-muted mt-2">
            <span>Created {formatDate(code.created_at)}</span>
            <span>Expires {formatDate(code.expires_at)}</span>
          </div>
        </div>

        <div className="flex flex-col items-end space-y-2 ml-4">
          {getStatusBadge()}

          {canRevoke && (
            <button
              onClick={() => onRevoke(code.id)}
              disabled={isRevoking}
              className="p-1.5 rounded-md hover:bg-clinic-accent/10 text-clinic-muted hover:text-clinic-accent transition-colors disabled:opacity-50"
              title="Revoke code"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </Card>
  );
};

const InviteCodeManager: React.FC = () => {
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data: InviteCodeListResponse = await inviteCodeAPI.listInviteCodes(showActiveOnly);
      setCodes(data.items);
      setActiveCount(data.active_count);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to load invite codes'));
      console.error('Error fetching invite codes:', err);
    } finally {
      setLoading(false);
    }
  }, [showActiveOnly]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleGenerate = async (data: InviteCodeCreate) => {
    try {
      setIsGenerating(true);
      const newCode = await inviteCodeAPI.createInviteCode(data);
      setCodes((prev) => [newCode, ...prev]);
      setActiveCount((prev) => prev + 1);
      setShowModal(false);
      toast.success(`Invite code ${newCode.formatted_code} generated!`);
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to generate code'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevoke = async (codeId: number) => {
    if (!window.confirm('Are you sure you want to revoke this code? This cannot be undone.')) {
      return;
    }

    try {
      setRevokingId(codeId);
      await inviteCodeAPI.revokeInviteCode(codeId);
      // Update local state
      setCodes((prev) =>
        prev.map((c) =>
          c.id === codeId
            ? { ...c, is_active: false, is_valid: false, revoked_at: new Date().toISOString() }
            : c
        )
      );
      setActiveCount((prev) => Math.max(0, prev - 1));
      toast.success('Invite code revoked');
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to revoke code'));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-clinic-dark font-body">Invite Codes</h1>
          <p className="text-clinic-muted mt-1">
            Generate invite codes to share with clients for private access to your calendar.
          </p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          leftIcon={<PlusIcon className="h-5 w-5" />}
        >
          Generate Code
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-primary/20 rounded-full flex items-center justify-center">
              <TicketIcon className="h-6 w-6 text-clinic-primary" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-clinic-muted">Active Codes</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : activeCount}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-gold/20 rounded-full flex items-center justify-center">
              <TicketIcon className="h-6 w-6 text-clinic-gold" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-clinic-muted">Total Generated</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : codes.length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-accent/20 rounded-full flex items-center justify-center">
              <CheckCircleIcon className="h-6 w-6 text-clinic-accent" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-clinic-muted">Used Codes</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : codes.filter((c) => c.times_used > 0).length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center justify-between mb-4">
        <label className="flex items-center text-sm text-clinic-muted cursor-pointer">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
            className="h-4 w-4 rounded border-clinic-primary-light text-clinic-primary focus:ring-clinic-primary mr-2"
          />
          Show active codes only
        </label>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-6">
          <StatusBanner
            variant="error"
            title="Unable to load codes"
            message={error}
            actions={
              <button
                onClick={fetchCodes}
                className="text-sm text-clinic-accent hover:underline"
              >
                Try again
              </button>
            }
          />
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary"></div>
        </div>
      ) : codes.length === 0 ? (
        /* Empty state */
        <Card className="text-center py-12">
          <TicketIcon className="mx-auto h-12 w-12 text-clinic-muted" />
          <h3 className="mt-2 text-lg font-medium text-clinic-dark">No invite codes</h3>
          <p className="mt-1 text-sm text-clinic-muted">
            {showActiveOnly
              ? 'No active codes. Generate a new one to share with clients.'
              : 'Generate your first invite code to share with clients.'}
          </p>
          <div className="mt-6">
            <Button
              onClick={() => setShowModal(true)}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Generate Code
            </Button>
          </div>
        </Card>
      ) : (
        /* Code list */
        <div className="space-y-3">
          {codes.map((code) => (
            <CodeCard
              key={code.id}
              code={code}
              onRevoke={handleRevoke}
              isRevoking={revokingId === code.id}
            />
          ))}
        </div>
      )}

      {/* Generate Code Modal */}
      <GenerateCodeModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onGenerate={handleGenerate}
        isGenerating={isGenerating}
      />
    </div>
  );
};

export default InviteCodeManager;
